// ── Reports surfaces (Wave 7 — REBUILT as gold-pattern pages, SECURITY) ─────
// The thin mock-only artifact surfaces (the reports parts of
// product-artifact-surfaces) are replaced by real pages: ReportsPage (the
// visibility-filtered library), ReportDetailPage (the report viewer with SAFE
// content + role-gated review + 503 download + SA-only regenerate), and
// SessionReportPage (the report through its session context). Each is wired via
// `customBody` so the route registration + provenance metadata is preserved.
//
// PARTICIPANT GENERATED-REPORT EXCLUSION (FR-REPORT-001): participant is excluded
// at the route (reportViewRoles) AND every page fail-closes. SA-only regeneration
// (FR-REPORT-003) renders the regenerate affordance for super_admin only. export ≤
// view is enforced by the D-3 filter + canRoleDownloadReport.

import type { ProductScreenContract } from "./product-route-contracts";
import { ReportsPage } from "./reports/reports-page";
import { ReportDetailPage } from "./reports/report-detail-page";
import { SessionReportPage } from "./reports/session-report-page";

const REPORTS_OWNER = "VIL-20260608-0022";

function focusedDefaults(): Pick<
  ProductScreenContract,
  "requiredViewports" | "surfaceMode" | "closureState"
> {
  return {
    requiredViewports: ["desktop", "mobile"],
    surfaceMode: "focused",
    closureState: "focused"
  };
}

export function reportLibraryScreen(): ProductScreenContract {
  return {
    title: () => "Reports",
    primaryJob:
      "List the role-visibility-filtered generated reports across the four types and open any report; never surface a report a role may not see.",
    secondaryJob:
      "Filter by type/status/cohort; participants are denied the module entirely (route + page fail-closed).",
    workTitle: "Report library",
    rows: [{ id: "report-1", report: "Session 1 Team Report", cohort: "Spring Leaders", status: "completed" }],
    columns: [
      { key: "report", header: "Report" },
      { key: "cohort", header: "Cohort" },
      { key: "status", header: "Status" }
    ],
    states: ["loading", "empty", "permission-denied", "unavailable", "error"],
    apiFamilies: ["artifact-service"],
    roleDeltas:
      "Staff see all four types; coach sees coach_feedback + team_admin; company_admin + cohort_lead see team_admin only; PARTICIPANT DENIED the module (fail-closed). Confidential/internal types never reach a non-staff surface.",
    extensionSlots: ["per-row signed download", "audience filter", "review queue", "report annotations"],
    mobilePosture: "Filter toolbar, count, and the reports table stack with no horizontal overflow.",
    evidenceRequirement:
      "Playwright must prove the visibility-filtered read, participant-denied fail-closed, every honest posture, and the row → viewer link.",
    vjalComponents: ["Badge","CollectionToolbar","IconDoc","PageFrame","ProTable","StatusDot"],
    localCompositions: ["reports focused product surface"],
    packageGapRefs: [],
    decisionHierarchy: {
      firstViewport: "Filter toolbar + the visibility-filtered reports table.",
      primaryAction: "Open a report for its content, review, and download.",
      secondaryPanel: "Type/status/cohort filters restricted to readable types.",
      deferredSurface: "Report content, review, download, and regenerate live on the viewer.",
      gatedControls: "Participant denied the module; coach/company_admin/cohort_lead see only their report types."
    },
    ...focusedDefaults(),
    closureOwner: REPORTS_OWNER,
    customBody: ReportsPage
  } as ProductScreenContract;
}

export function reportDetailScreen(): ProductScreenContract {
  return {
    title: () => "Report detail",
    primaryJob:
      "Read one authorized report (content rendered safely), review/approve per role, download (gate-closed honest state), and regenerate (SA-only).",
    secondaryJob:
      "Content is plain text/arrays rendered with React escaping — never raw HTML injection. Participant is denied; confidential types are unreachable by non-staff.",
    workTitle: "Report viewer",
    rows: [{ id: "report-1", section: "Content", status: "ready" }],
    columns: [
      { key: "section", header: "Section" },
      { key: "status", header: "Status" }
    ],
    states: ["loading", "empty", "permission-denied", "unavailable", "gate-closed", "not-configured", "error"],
    apiFamilies: ["artifact-service"],
    roleDeltas:
      "company_admin/coach may mark-reviewed; approve is STAFF-ONLY; regenerate is SUPER-ADMIN-ONLY (and not-configured — no user endpoint); cohort_lead is download-only; participant DENIED.",
    extensionSlots: ["signed download", "review audit", "report-agent regenerate (open dependency)", "report annotations"],
    mobilePosture: "Header, content, review/download/regenerate panels stack with peer privacy and no overflow.",
    evidenceRequirement:
      "Playwright must prove SAFE content render, the review role matrix, the 503 gate-closed download, the SA-only regenerate, and participant denial.",
    vjalComponents: ["Alert","Badge","Button","DetailSurface","Module","ModuleBody","ModuleHead"],
    localCompositions: ["report-detail focused product surface", "CapabilityButton"],
    packageGapRefs: [],
    decisionHierarchy: {
      firstViewport: "Report identity header + audience/status badges.",
      primaryAction: "Read the report content, then mark reviewed / approve per role.",
      secondaryPanel: "Download (gate-closed honest state) + SA-only regenerate (not-configured).",
      deferredSurface: "File download + user-triggered regenerate land with their operator-gated lanes.",
      gatedControls: "Approve staff-only; regenerate SA-only; download respects view scope; participant denied."
    },
    ...focusedDefaults(),
    closureOwner: REPORTS_OWNER,
    customBody: ReportDetailPage
  } as ProductScreenContract;
}

export function sessionReportScreen(): ProductScreenContract {
  return {
    title: () => "Session report",
    primaryJob:
      "Read the report through its session context, proving the report belongs to the visible session, with the same safe content + role-gated actions.",
    secondaryJob: "Participants are denied; out-of-scope session reports resolve generic-not-found.",
    workTitle: "Session report viewer",
    rows: [{ id: "session-1", section: "Report", status: "ready" }],
    columns: [
      { key: "section", header: "Section" },
      { key: "status", header: "Status" }
    ],
    states: ["loading", "empty", "permission-denied", "unavailable", "gate-closed", "not-configured", "error"],
    apiFamilies: ["artifact-service", "session-service"],
    roleDeltas:
      "Same review/download/regenerate role matrix as the report viewer; participant DENIED the module; confidential types unreachable by non-staff.",
    extensionSlots: ["signed download", "session report annotations", "report-agent regenerate (open dependency)"],
    mobilePosture: "Session context, content, and role-gated actions stack with no overflow.",
    evidenceRequirement:
      "Playwright must prove the session-scoped read, SAFE content, the role matrix, the 503 download, and participant denial.",
    vjalComponents: ["Alert","Badge","Button","DetailSurface","Module","ModuleBody","ModuleHead"],
    localCompositions: ["session-report focused product surface", "CapabilityButton"],
    packageGapRefs: [],
    decisionHierarchy: {
      firstViewport: "Session report identity header + audience/status badges.",
      primaryAction: "Read the report content, then mark reviewed / approve per role.",
      secondaryPanel: "Download (gate-closed) + SA-only regenerate (not-configured).",
      deferredSurface: "File download + user-triggered regenerate land with their operator-gated lanes.",
      gatedControls: "Approve staff-only; regenerate SA-only; download respects view scope; participant denied."
    },
    ...focusedDefaults(),
    closureOwner: REPORTS_OWNER,
    customBody: SessionReportPage
  } as ProductScreenContract;
}
